import axios from 'axios';
import { ApiResponse, GetReportResponse, zps_ea_war, zps_wp } from '../zps';
import { handleDownload } from '../../app/utils/script';
import { PeriodTotals } from '../dashboardV2';

export type IEquipment = {
  almostFullPerc: number;
  almostFullValue: number;
  bag: boolean;
  bankid: number;
  cardInsertionTimeout: number;
  cardRemovalTimeout: number;
  comuid: number;
  currencies: IEquipmentCurrency[];
  dataInsertionTimeout: number;
  declarationTimeout: number;
  depositTimeout: number;
  description: string;
  eid: number;
  entid: number;
  equiId: number;
  etid: number;
  etvid: number;
  fullPerc: number;
  fullValue: number;
  fullValueCid: number;
  gid: number;
  idTerminal: number;
  inuse: number;
  lang: number;
  languages: IEquipmentLanguage[];
  lid: number;
  maintid: number;
  maintid2: number;
  maxBills: number;
  maxCoins: number;
  maxCurrencies: number;
  p43sf8: number;
  region: string;
  sa1: number;
  sa2: number;
  sa3: number;
  sa4: number;
  sa5: number;
  sa6: number;
  sa7: number;
  sa8: number;
  secur: number;
  serialnumber: string;
  sid: number;
  status: number;
  suppid: number;
  token: string;
  zkey: string;
  amount: string;
  lat?: number;
  lng?: number;
};

export type IEquipmentState = {
  cardreaderState: number;
  depositToRecover: boolean;
  diskUsage: number;
  equipmentState: number;
  fakeNoteDetected: boolean;
  moosState: number;
  paySystemState: number;
  printerState: number;
  quantityState: number;
  switchs: number;
  upsState: number;
  valueState: number;
};

export type IEquipmentLanguage = {
  eid: number;
  gid: number;
  langid: number;
  langidx: number;
};

export type IEditEquipmentCurrencyRequest = {
  eid: number;
  currency: IEquipmentCurrency;
};

export type IEquipmentDenomination = {
  existRecycler?: number;
  existSafe?: number;
  existTotal?: number;
  index: number;
  type?: number;
  value?: number;
  wc: number;
  wcLacking?: number;
  wcSurplus?: number;
  weight?: number;
};

export type IEquipmentCurrency = {
  cid: number;
  eid?: number;
  gid?: number;
  maxAmount?: number;
  minAmount?: number;
  denominations?: IEquipmentDenomination[];
  description?: string;
  factor?: number;
};

export type IEntityLabel = {
  entid?: number;
  name?: string;
  label?: string;
};

export type IEquipmentDetails = {
  equipment: IEquipment;
  equipmentState: IEquipmentState;
  lastComDt: string;
  lastLocalIp: string;
  lastOperation: string;
  lastOperationDt: string;
  period: number;
  periodAmounts?: PeriodTotals[];
  softwareVersion: string;
  safeOccPerc?: number;

  location?: string;
  amount?: string;
  position?: google.maps.LatLngLiteral;
};

export type IEquipmentOption = {
  eid: number;
  label: string;
};

export type IEquipmentDeviceStatus = {
  id: string;
  idLangKey: string;
  level: string;
  statusLangKey: string;
};

export type IRemoteOperation = {
  eid: number;
  operation: string;
};

export interface IEquipmentStatusResponse extends ApiResponse {
  status: {
    inService: boolean;
    lastComDt: string;
    lastLocalIp: string;
    lastOperation: string;
    lastOperationDt: string;
    softwareVersion: string;
    status: number;
    statusList?: IEquipmentDeviceStatus[];
  };
}

export interface IEquipmentDetailsResponse extends ApiResponse {
  equipments: IEquipmentDetails[];
}

export interface IEquipmentListResponse extends ApiResponse {
  equipments: IEquipmentDetails[];
  totalRows: number;
  type: string;
}

export interface IEquipmentOptionsResponse extends ApiResponse {
  list: IEquipmentOption[];
  type: string;
}

export interface GetEquipmentCurrencyResponse extends ApiResponse {
  list: IEquipmentCurrency[];
  type: string;
}

export interface IHelpdeskEquipmentResponse extends ApiResponse {
  list: IEquipmentDetails[];
  totalRows: number;
  type: string;
}

export type Reason = {
  gid: number;
  entid: number;
  langid: number;
  code: number;
  reason: string;
};

export type Declaration = {
  gid: number;
  entid: number;
  langid: number;
  code: number;
  declaration: string;
};

export interface EquipmentReasonsResponse extends ApiResponse {
  reasons: Reason[];
  type: string;
}

export interface EquipmentDeclarationResponse extends ApiResponse {
  declarations: Declaration[];
  type: string;
}

export interface AddEquipmentRequest {
  entid: number;
  equipmentFile: string;
}

export interface EquipmentUpdateRequest {
  almostFullPerc?: number;
  almostFullValue?: number;
  almostFullValueToShow?: number;
  amounts?: [];
  bankid?: number;
  cardInsertionTimeout?: number;
  cardRemovalTimeout?: number;
  currencies?: [];
  dataInsertionTimeout?: number;
  declarationTimeout?: number;
  depositTimeout?: number;
  description: string;
  eid?: number;
  entid?: number;
  fullPerc?: number;
  fullValue?: number;
  fullValueCid?: number;
  fullValueCidObj?: number;
  fullValueToShow?: number;
  lang: number;
  languages?: [];
  lastComDt?: string;
  lastOperationDt?: string;
  maxBills?: number;
  maxCoins?: number;
  period?: string;
  region?: string;
  sid?: number;
  status?: string;
  type?: number;
  _status?: number;
}

export const fetchEquipmentList = (
  controller: AbortController,
  entid: number,
  sid: number,
  offset: number,
  limit: number,
  totalRows: boolean
): Promise<IEquipmentListResponse> => {
  return axios
    .get<IEquipmentListResponse>(`${zps_wp}/api/equipment/details`, {
      params: { entid, sid, offset, limit, totalRows },
      signal: controller.signal
    })
    .then(response => response.data);
};

export const updateEquipment = (request: EquipmentUpdateRequest, controller: AbortController): Promise<ApiResponse> => {
  return axios
    .put<ApiResponse>(`${zps_ea_war}/equipment`, request, { signal: controller.signal })
    .then(response => response.data);
};

export const fetchEquipmentStatus = (eid: number): Promise<IEquipmentStatusResponse> => {
  return axios
    .get<IEquipmentStatusResponse>(`${zps_wp}/api/equipment/status`, { params: { eid } })
    .then(response => response.data);
};

export const fetchEquipmentOptions = (
  entid: number,
  sid: number,
  controller: AbortController
): Promise<IEquipmentOptionsResponse> => {
  return axios
    .get<IEquipmentOptionsResponse>(`${zps_ea_war}/equipment/options/`, {
      params: { entid, sid },
      signal: controller.signal
    })
    .then(response => response.data);
};

export const fetchEquipmentCurrency = (
  controller: AbortController,
  eid: number
): Promise<GetEquipmentCurrencyResponse> => {
  return axios
    .get<GetEquipmentCurrencyResponse>(`${zps_ea_war}/equipment/currency/`, {
      params: { eid },
      signal: controller.signal
    })
    .then(response => response.data);
};

export const fetchEquipmentDetails = (controller: AbortController, eid: number): Promise<IEquipmentDetailsResponse> => {
  return axios
    .get<IEquipmentDetailsResponse>(`${zps_ea_war}/equipment/details/`, {
      params: { eid },
      signal: controller.signal
    })
    .then(res => res.data);
};

export const fetchEquipmentReasons = (
  controller: AbortController,
  entid: number
): Promise<EquipmentReasonsResponse> => {
  return axios
    .get<EquipmentReasonsResponse>(`${zps_ea_war}/reason/`, { params: { entid }, signal: controller.signal })
    .then(response => response.data);
};

export const fetchEquipmentDeclarations = (
  controller: AbortController,
  entid: number
): Promise<EquipmentDeclarationResponse> => {
  return axios
    .get<EquipmentDeclarationResponse>(`${zps_ea_war}/declaration/`, { params: { entid }, signal: controller.signal })
    .then(response => response.data);
};

export const addEquipment = (request: AddEquipmentRequest, controller: AbortController): Promise<ApiResponse> => {
  return axios
    .post<ApiResponse>(`${zps_ea_war}/equipment/addbyfile/`, request, { signal: controller.signal })
    .then(response => response.data);
};

export const addEquipmentReason = (controller: AbortController, payload: Reason): Promise<ApiResponse> => {
  return axios.post<ApiResponse>(`${zps_ea_war}/reason/`, payload, { signal: controller.signal }).then(res => res.data);
};

export const addEquipmentDeclaration = (controller: AbortController, payload: Declaration): Promise<ApiResponse> => {
  return axios
    .post<ApiResponse>(`${zps_ea_war}/declaration/`, payload, { signal: controller.signal })
    .then(res => res.data);
};

export const updateEquipmentReason = (controller: AbortController, payload: Reason): Promise<ApiResponse> => {
  return axios.put<ApiResponse>(`${zps_ea_war}/reason/`, payload, { signal: controller.signal }).then(res => res.data);
};

export const updateEquipmentDeclaration = (controller: AbortController, payload: Declaration): Promise<ApiResponse> => {
  return axios
    .put<ApiResponse>(`${zps_ea_war}/declaration/`, payload, { signal: controller.signal })
    .then(res => res.data);
};

export const updateEquipmentWorkingCapital = (
  controller: AbortController,
  payload: IEditEquipmentCurrencyRequest
): Promise<ApiResponse> => {
  return axios
    .put<ApiResponse>(`${zps_ea_war}/equipment/currency`, payload, { signal: controller.signal })
    .then(res => res.data);
};

export const deleteEquipmentReason = (entid: number, code: number, langid: number): Promise<ApiResponse> => {
  return axios.delete<ApiResponse>(`${zps_ea_war}/reason/`, { params: { entid, code, langid } }).then(res => res.data);
};

export const deleteEquipmentDeclaration = (entid: number, code: number, langid: number): Promise<ApiResponse> => {
  return axios
    .delete<ApiResponse>(`${zps_ea_war}/declaration/`, { params: { entid, code, langid } })
    .then(res => res.data);
};

export const equipmentRemoteOperation = (
  controller: AbortController,
  payload: IRemoteOperation
): Promise<ApiResponse> => {
  return axios
    .post<ApiResponse>(`${zps_ea_war}/remoteoperation/`, payload, { signal: controller.signal })
    .then(res => res.data);
};

export const fetchExistencesReport = (
  eid: number,
  period: number,
  controller: AbortController
): Promise<GetReportResponse> => {
  return axios
    .get<GetReportResponse>(`${zps_ea_war}/equipment/details/report/existences/`, {
      params: { eid, period },
      signal: controller.signal
    })
    .then(res => {
      handleDownload(res.data.report, res.data.name, res.data.mime);
      return res.data;
    })
    .catch(err => {
      console.error('Failed to fetch Report file:', err);
      throw err;
    });
};

export const fetchEquipmentWCXLS = (controller: AbortController, eid: number): Promise<GetReportResponse> => {
  return axios
    .get<GetReportResponse>(`${zps_ea_war}/equipment/currency/report`, {
      params: { format: 'xls', eid },
      signal: controller.signal
    })
    .then(res => {
      handleDownload(res.data.report, res.data.name, res.data.mime);
      return res.data;
    })
    .catch(err => {
      console.error('Failed to fetch Report file:', err);
      throw err;
    });
};
